import {Injectable} from '@angular/core';

/**
 * Service to interact with the Screen Enumeration API.
 * This provider checks permissions for accessing screen details and fetches
 * information about connected displays.
 *
 * @see {@link https://developer.chrome.com/docs/capabilities/web-apis/window-management?hl=pt-br} for API details.
 * @see {@link https://angular.io/guide/dependency-injection} for more on Angular Dependency Injection.
 */
@Injectable()
export class ScreenDetailsProvider {

  /**
   * Retrieves the number of connected displays and their details.
   *
   * @returns {Promise<any>} A promise that resolves with screen details or
   *                         an error message if permissions are denied or unsupported.
   */
  public getQuantity(): Promise<any> {
    return new Promise((resolve, reject): void => {
      // Check if the Screen Enumeration API and Permissions API are supported
      if ('getScreenDetails' in window && navigator.permissions) {
        navigator.permissions.query({name: 'window-management'}).then((permissionStatus: PermissionStatus): void => {
          switch (permissionStatus.state) {
            case 'granted':
            case 'prompt': // Handle cases where user can proceed
              this.fetchScreenInfo().then(resolve).catch(reject);
              break;
            case 'denied': // Handle denied permission state
              resolve(this.createPermissionDeniedResult());
              break;
            default: // Handle unexpected permission states
              resolve(this.createUnknownStateResult());
              break;
          }
        }).catch(error => {
          // Handle errors querying permissions
          reject(this.createPermissionErrorResult(error));
        });
      } else {
        // Handle unsupported Screen Enumeration API
        resolve(this.createUnsupportedApiResult());
      }
    });
  }

  /**
   * Fetches detailed screen information using the Screen Enumeration API.
   *
   * @private
   * @returns {Promise<any>} A promise that resolves with the screen details or rejects with an error.
   */
  private fetchScreenInfo(): Promise<any> {
    return new Promise((resolve, reject): void => {
      (window as any).getScreenDetails().then((screenDetails: any): void => {
        const screens = screenDetails.screens;
        const numberOfDisplays = screens.length;
        resolve({
          code: 200,
          message: numberOfDisplays > 1 ? 'Multiple displays detected.' : 'Single display detected.',
          quantity: numberOfDisplays
        });
      }).catch(error => {
        console.error('Error accessing screens:', error);
        reject({
          code: 500,
          message: 'Unable to access screen information. Error accessing screens.',
          error: error
        });
      });
    });
  }

  /**
   * Creates a result object for denied permissions.
   *
   * @private
   * @returns {any} An object with code and message indicating permission denial.
   */
  private createPermissionDeniedResult(): any {
    return {
      code: 401,
      message: 'Permission to access screen information was denied.'
    };
  }

  /**
   * Creates a result object for permission query errors.
   *
   * @private
   * @param {any} error - The error object from the permissions query.
   * @returns {any} An object with code, message, and error details.
   */
  private createPermissionErrorResult(error: any): any {
    console.error('Permission query failed:', error);
    return {
      code: 500,
      message: 'Unable to query permissions. Permission query failed.',
      error: error
    };
  }

  /**
   * Creates a result object for unsupported APIs.
   *
   * @private
   * @returns {any} An object with code and message indicating API unsupported.
   */
  private createUnsupportedApiResult(): any {
    return {
      code: 404,
      message: 'API window.getScreenDetails() is not supported in this browser.'
    };
  }

  /**
   * Creates a result object for unknown permission states.
   *
   * @private
   * @returns {any} An object with code and message indicating an unknown state.
   */
  private createUnknownStateResult(): any {
    return {
      code: 400,
        message: 'Unknown permission state encountered.'
    };
  }

}



// WEBPACK FOOTER //
// ./src/providers/screen-details/screen-details.ts